import { useApi, githubAuthApiRef } from '@backstage/core-plugin-api';
import { useEffect, useMemo, useState } from 'react';

const TOKEN_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // Refresh every 5 minutes
const RETRY_INTERVAL_MS = 30 * 1000; // Retry after 30 seconds on error

/**
 * Fetches a GitHub OAuth user token via the Backstage GitHub auth API and
 * keeps it refreshed in the background.
 *
 * The caller is responsible for choosing the minimum set of OAuth scopes
 * required for its use case (principle of least privilege). See
 * https://docs.github.com/en/apps/oauth-apps/building-oauth-apps/scopes-for-oauth-apps
 * for the list of available scopes.
 *
 * Note: scope sets are part of the auth session cache key, so requesting
 * different scope sets across the app may force the user to re-consent when
 * switching between features.
 *
 * @param scopes - Required GitHub OAuth scopes (e.g. `['repo']`).
 */
export function useGitHubToken(scopes: string[]) {
  const githubAuth = useApi(githubAuthApiRef);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);

  // Stable key for the requested scopes so that callers passing an inline
  // array literal don't cause the effect to re-run on every render.
  const scopesKey = useMemo(() => scopes.slice().sort().join(' '), [scopes]);

  useEffect(() => {
    let cancelled = false;

    const fetchToken = async () => {
      try {
        // getAccessToken silently refreshes if the token is near expiry.
        // optional: false triggers the GitHub OAuth flow if no session exists
        // instantPopup: false ensures we don't trigger a new login flow on every render
        const accessToken = await githubAuth.getAccessToken(scopesKey, {
          optional: false,
          instantPopup: false,
        });

        if (!cancelled) {
          setToken(accessToken);
          setError(undefined);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e as Error);
        }
      }
    };

    fetchToken();
    const intervalId = setInterval(
      fetchToken,
      error ? RETRY_INTERVAL_MS : TOKEN_REFRESH_INTERVAL_MS,
    );

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [githubAuth, error, scopesKey]);

  return { token, error };
}
